import { supabase } from '@/lib/supabase';
import { invokeEdge } from '@/lib/edgeInvoke';
import { runBrowseRegression } from '@/lib/browseRegression';
import {
  AreaId,
  CheckVerdict,
  RELEASE_CHECKS,
  ReleaseCheck,
  VerdictStatus,
  checksForAreas,
} from '@/data/releaseChecks';

/**
 * RUNNER + DURABLE RECORD for the pre-deploy validation gate.
 *
 * The registry (src/data/releaseChecks.ts) says WHICH suites a change needs.
 * This module runs them and writes the verdict to `mkt_release_runs` so there
 * is an auditable answer to "was this validated before it shipped?" months
 * later. Reads and inserts are admin-only at the database level.
 *
 * A suite that cannot be reached is recorded as 'error' — never as a pass.
 */

export type ResultStatus = VerdictStatus | 'error';

export interface CheckResult {
  id: string;
  status: ResultStatus;
  summary: string;
  detail: string[];
  ms: number;
}

export interface ReleaseRun {
  id?: string;
  created_at?: string;
  run_by_email?: string | null;
  areas: AreaId[];
  change_note?: string | null;
  total_checks: number;
  passed_checks: number;
  failed_checks: number;
  attention_checks: number;
  verdict: 'clear' | 'blocked' | 'review' | 'unknown';
  results: CheckResult[];
}

const errorResult = (id: string, message: string, ms: number): CheckResult => ({
  id,
  status: 'error',
  summary: message,
  detail: ['The suite did not return a verdict, so this area is NOT validated.'],
  ms,
});

/** Run one suite and interpret its answer. Never throws. */
export const runCheck = async (check: ReleaseCheck): Promise<CheckResult> => {
  const started = Date.now();
  try {
    let verdict: CheckVerdict;

    if (check.kind === 'client') {
      if (check.id === 'browse-regression') {
        verdict = check.interpret(await runBrowseRegression());
      } else {
        return errorResult(check.id, 'No client runner is registered for this check.', Date.now() - started);
      }
    } else {
      const res = await invokeEdge(check.fn as string, check.body || {}, {
        requireAuth: check.requireAuth !== false,
      });
      // 423 is this project's established "harness intentionally disarmed in
      // production" answer (see the note security-verify records for the
      // endpoints it probes). A disarmed suite has NOT validated anything, so
      // it is reported as needing attention — never as a pass.
      if (res.status === 423) {
        return {
          id: check.id,
          status: 'attention',
          summary: 'The harness is disarmed in production — this suite did not run.',
          detail: ['Arm it in mkt_settings, run it again, then review the result before shipping.'],
          ms: Date.now() - started,
        };
      }
      if (!res.ok || !res.data) {
        return errorResult(
          check.id,
          res.message || `The ${check.fn} function did not answer (HTTP ${res.status ?? '?'}).`,
          Date.now() - started,
        );
      }
      verdict = check.interpret(res.data);
    }

    return { id: check.id, ...verdict, ms: Date.now() - started };
  } catch (e) {
    return errorResult(check.id, (e as Error)?.message || 'The suite threw an error.', Date.now() - started);
  }
};

export const verdictFor = (results: CheckResult[]): ReleaseRun['verdict'] => {
  if (!results.length) return 'unknown';
  if (results.some((r) => r.status === 'fail' || r.status === 'error')) return 'blocked';
  if (results.some((r) => r.status === 'attention')) return 'review';
  return 'clear';
};

/** Persist the run. Failure to record never hides the verdict on screen. */
export const saveRun = async (run: {
  areas: AreaId[];
  changeNote: string;
  results: CheckResult[];
}): Promise<void> => {
  const { data: auth } = await supabase.auth.getUser();
  const results = run.results;
  await supabase.from('mkt_release_runs').insert({
    run_by: auth?.user?.id || null,
    run_by_email: auth?.user?.email || null,
    areas: run.areas,
    change_note: run.changeNote ? run.changeNote.slice(0, 500) : null,
    total_checks: results.length,
    passed_checks: results.filter((r) => r.status === 'pass').length,
    failed_checks: results.filter((r) => r.status === 'fail' || r.status === 'error').length,
    attention_checks: results.filter((r) => r.status === 'attention').length,
    verdict: verdictFor(results),
    results,
  });
};

export const loadRuns = async (limit = 8): Promise<ReleaseRun[]> => {
  const { data } = await supabase
    .from('mkt_release_runs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  return (data || []) as ReleaseRun[];
};

/** Every suite, ordered fast-first so failures surface sooner. */
export const orderedChecksForAreas = (areas: AreaId[]): ReleaseCheck[] => {
  const required = areas.length ? checksForAreas(areas) : RELEASE_CHECKS;
  return [...required].sort((a, b) => (a.duration === b.duration ? 0 : a.duration === 'fast' ? -1 : 1));
};
